import { Suspense, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function HeatingControlsPage() {
  useEffect(() => {
    document.title = 'Heating Controls & Thermostats Watford | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional heating controls and smart thermostat installation in Watford. Save energy with programmable heating. Call 020 3198 1818 for a free quote.');
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical Services",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "telephone": "020 3198 1818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Watford",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "WD17",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "url": "https://beyondelectrical.co.uk/services/installations/heating-controls",
      "areaServed": "Watford",
      "serviceType": "Heating Controls & Thermostats",
      "openingHours": "Mo-Su 00:00-23:59"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-amber-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm mb-6">
                <i className="ri-map-pin-line text-amber-500"></i>
                <span className="text-sm font-medium text-gray-700">Serving Watford & Surrounding Areas</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Heating Controls & Thermostats <span className="text-amber-500">in Watford</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Professional installation of smart thermostats and heating controls. Save energy, reduce bills, and enjoy perfect comfort throughout Watford and Rickmansworth.
              </p>
              
              <div className="flex flex-wrap gap-4 justify-center mb-8">
                <a href="tel:02031981818" className="inline-flex items-center gap-2 bg-amber-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                  <i className="ri-phone-line text-xl"></i>
                  Call 020 3198 1818
                </a>
                <button onClick={scrollToContact} className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                  <i className="ri-mail-line text-xl"></i>
                  Get Free Quote
                </button>
              </div>
              
              <div className="flex flex-wrap gap-6 justify-center text-sm">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-amber-500 text-xl"></i>
                  <span className="text-gray-700 font-medium">NICEIC Certified</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-amber-500 text-xl"></i>
                  <span className="text-gray-700 font-medium">15 Years Experience</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-money-pound-circle-line text-amber-500 text-xl"></i>
                  <span className="text-gray-700 font-medium">Save Up to 30%</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Thermostat Options */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Smart Thermostat Options</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Choose the perfect heating control system for your home
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-leaf-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Nest Learning Thermostat</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Learns your schedule and adjusts automatically. Energy history and remote control via app.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-amber-500 mt-0.5"></i>
                    <span>Self-learning technology</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-amber-500 mt-0.5"></i>
                    <span>Energy reports & savings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-amber-500 mt-0.5"></i>
                    <span>Works with Alexa & Google</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-home-wifi-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Hive Active Heating</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  British-designed smart heating with simple app control and flexible scheduling.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-amber-500 mt-0.5"></i>
                    <span>Easy-to-use app interface</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-amber-500 mt-0.5"></i>
                    <span>Holiday mode & boost</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-amber-500 mt-0.5"></i>
                    <span>Multi-zone control available</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-temp-hot-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Tado Smart Thermostat</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Geofencing technology detects when you're away and adjusts heating automatically.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-amber-500 mt-0.5"></i>
                    <span>Location-based control</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-amber-500 mt-0.5"></i>
                    <span>Weather adaptation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-amber-500 mt-0.5"></i>
                    <span>Room-by-room control</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Features & Benefits */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Smart Heating Features</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Advanced features that save energy and improve comfort
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-smartphone-line text-xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Remote Control</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Adjust heating from anywhere using your smartphone, tablet, or computer.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-calendar-line text-xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Smart Scheduling</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Create custom heating schedules for different days and times automatically.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-map-pin-user-line text-xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Geofencing</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Heating turns down when you leave and warms up before you arrive home.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-line-chart-line text-xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Energy Reports</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Track your energy usage and see how much you're saving each month.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-voice-recognition-line text-xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Voice Control</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Control temperature with Alexa, Google Assistant, or Siri voice commands.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-home-gear-line text-xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Multi-Zone Control</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Set different temperatures for different rooms with smart radiator valves.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Savings Calculator */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-12 text-white text-center">
              <h2 className="text-4xl font-bold mb-6">Potential Annual Savings</h2>
              <div className="grid md:grid-cols-3 gap-8 mb-8">
                <div>
                  <div className="text-5xl font-bold mb-2">30%</div>
                  <p className="text-amber-100">Energy Reduction</p>
                </div>
                <div>
                  <div className="text-5xl font-bold mb-2">£300+</div>
                  <p className="text-amber-100">Average Savings</p>
                </div>
                <div>
                  <div className="text-5xl font-bold mb-2">2 Years</div>
                  <p className="text-amber-100">Payback Period</p>
                </div>
              </div>
              <p className="text-lg text-amber-50 leading-relaxed">
                Based on average UK household heating costs. Smart thermostats typically pay for themselves within 2 years through energy savings.
              </p>
            </div>
          </div>
        </section>

        {/* Installation Process */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Installation Process</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Professional installation completed in just a few hours
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">1</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">System Check</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  We assess your current heating system and recommend the best thermostat.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">2</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Installation</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Professional wiring and mounting of your new smart thermostat.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">3</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Setup & Config</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  WiFi connection, app setup, and initial schedule configuration.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">4</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Training</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Full demonstration of features and how to get the most from your system.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Beyond Electrical?</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Expert heating control installation in Watford
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-award-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">15 Years Experience</h3>
                <p className="text-gray-600 leading-relaxed">
                  Extensive experience with all major smart thermostat brands and heating systems.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-shield-star-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">NICEIC Certified</h3>
                <p className="text-gray-600 leading-relaxed">
                  All installations certified and compliant with electrical safety regulations.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-customer-service-2-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Ongoing Support</h3>
                <p className="text-gray-600 leading-relaxed">
                  Local support team available to help with any questions or adjustments.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Areas Covered */}
        <section className="py-16 bg-amber-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Areas We Cover</h2>
              <div className="flex flex-wrap justify-center gap-4 text-gray-700">
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Watford</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Rickmansworth</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Bushey</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Abbots Langley</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Croxley Green</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Chorleywood</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Northwood</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Radlett</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Borehamwood</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">St Albans</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Hemel Hempstead</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Harrow</span>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section id="contact" className="py-20 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-6">Ready to Upgrade Your Heating Controls?</h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Get a free quote for smart thermostat installation in Watford
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="tel:02031981818" className="inline-flex items-center gap-2 bg-amber-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line text-xl"></i>
                Call 020 3198 1818
              </a>
              <a href="mailto:info@beyondelectrical.co.uk" className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                <i className="ri-mail-line text-xl"></i>
                Email Us
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </Suspense>
  );
}